<template>
  <div
    class="flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-gray-100 bg-white shadow-sm p-6"
  >
    <div
      class="relative shrink-0"
      :style="{ width: svgSize + 'px', height: svgSize + 'px' }"
      :title="npsTitle"
    >
      <svg
        :width="svgSize"
        :height="svgSize"
        class="absolute top-0 left-0 -rotate-90"
        aria-hidden="true"
      >
        <circle
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="NEUTRAL_RING_COLOR"
          :stroke-width="STROKE_WIDTH"
          fill="none"
        />
        <circle
          v-if="clampedScore > 0"
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="arcColor"
          :stroke-width="STROKE_WIDTH"
          fill="none"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="offset"
          stroke-linecap="round"
        />
      </svg>
      <v-avatar
        color="primary"
        :size="AVATAR_SIZE"
        class="absolute"
        :style="{ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' }"
      >
        <ProtectedAvatar :src="avatarSrc" :size="AVATAR_SIZE">
          <span class="text-2xl font-bold">{{ initials }}</span>
        </ProtectedAvatar>
      </v-avatar>
    </div>

    <div class="flex min-w-0 flex-1 flex-col items-center sm:items-start gap-2">
      <h1 class="text-2xl font-bold text-gray-900 text-center sm:text-left">{{ doctorName }}</h1>

      <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2">
        <LabelBadge
          data-testid="doctor-header-crm"
          :label="crmLabel"
          :color="crmStatus?.color ?? 'blue'"
          :icon="null"
          size="small"
        />
      </div>

      <div
        class="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 text-sm text-gray-500"
      >
        <span class="flex items-center gap-1" data-testid="doctor-header-companies">
          <SVGIcon :path="mdiOfficeBuildingOutline" />
          {{ companiesLabel }}
        </span>
        <span class="flex items-center gap-1" data-testid="doctor-header-specialties">
          <SVGIcon :path="mdiStethoscope" />
          {{ specialtiesLabel }}
        </span>
      </div>
    </div>

    <!-- O anel do avatar é o NPS; o quadro explica o número e a faixa. -->
    <div
      class="flex min-w-[6.5rem] shrink-0 flex-col items-center gap-0.5 text-center rounded-xl border border-gray-100 bg-gray-50 px-5 py-3"
      data-testid="doctor-header-nps"
    >
      <p class="text-[11px] font-medium uppercase tracking-wide text-gray-500">NPS</p>
      <p class="text-2xl font-bold leading-8" :style="{ color: npsClassification?.color }">
        {{ npsScore ?? '—' }}
      </p>
      <p class="text-xs text-gray-500">{{ npsClassification?.label ?? 'Sem avaliações' }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiOfficeBuildingOutline, mdiStethoscope } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import {
  getCrmStatusConfig,
  getInitials,
  resolveAvatarUrl,
  toTitleCase
} from '../../helpers/doctorStatus'

const props = defineProps<{
  doctor: DoctorDetailResponse
}>()

const doctorName = computed(() =>
  toTitleCase(props.doctor.doctor.user?.name || props.doctor.doctor.mainCrm.name)
)

const avatarSrc = computed(() => resolveAvatarUrl(props.doctor.doctor.user?.avatarUrl))

const initials = computed(() => getInitials(doctorName.value))

const AVATAR_SIZE = 80
const STROKE_WIDTH = 4
const radius = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const svgSize = radius * 2 + STROKE_WIDTH
const circumference = 2 * Math.PI * radius

const npsScore = computed(() => props.doctor.doctor.npsScore ?? null)
const npsClassification = computed(() =>
  npsScore.value === null ? null : classifyNpsScore(npsScore.value)
)
const npsTitle = computed(() =>
  npsClassification.value ? `NPS ${npsScore.value} (${npsClassification.value.label})` : 'Sem NPS'
)

const clampedScore = computed(() => {
  const score = npsScore.value
  if (score == null) return 0
  return Math.min(100, Math.max(0, (score + 100) / 2))
})

const offset = computed(() => circumference * (1 - clampedScore.value / 100))

const arcColor = computed(() => npsClassification.value?.color ?? NEUTRAL_RING_COLOR)

const crmStatus = computed(() => getCrmStatusConfig(props.doctor.doctor.mainCrm.status))
const crmLabel = computed(() => {
  const { number, uf } = props.doctor.doctor.mainCrm
  return crmStatus.value ? `CRM ${number}-${uf} · ${crmStatus.value.label}` : `CRM ${number}-${uf}`
})

const companiesLabel = computed(() => {
  const count = props.doctor.companies.length
  return count === 1 ? '1 empresa vinculada' : `${count} empresas vinculadas`
})

const specialtiesLabel = computed(() => {
  const count = props.doctor.doctor.mainCrm.doctorSpecialties?.length ?? 0
  return count === 1 ? '1 especialidade' : `${count} especialidades`
})
</script>
